import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, CheckIcon, SendIcon, CheckCircle2Icon, CameraIcon, XIcon, ImagePlusIcon } from 'lucide-react';
import { loadSizes, jobTypes, timings } from '../data/quote';
import { serviceAreas, PHONE_DISPLAY, PHONE_HREF } from '../data/content';
const fieldClass = 'w-full rounded-md border border-forest-100 bg-white px-4 py-3 text-base text-ink-900 placeholder:text-ink-700/50 focus:border-forest-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-ember-500';
export function QuoteForm() {
  const [selectedJobs, setSelectedJobs] = useState<string[]>([jobTypes[0]]);
  const [sizeId, setSizeId] = useState(loadSizes[1].id);
  const [city, setCity] = useState(serviceAreas[0]);
  const [timing, setTiming] = useState(timings[0]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [details, setDetails] = useState('');
  const [photos, setPhotos] = useState<{
    id: string;
    url: string;
    name: string;
  }[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const MAX_PHOTOS = 4;
  useEffect(() => () => {
    photos.forEach(photo => URL.revokeObjectURL(photo.url));
  }, [photos]);
  const addPhotos = (fileList: FileList | null) => {
    if (!fileList) return;
    const incoming = Array.from(fileList).filter(file => file.type.startsWith('image/')).slice(0, MAX_PHOTOS - photos.length).map(file => ({
      id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2, 7)}`,
      url: URL.createObjectURL(file),
      name: file.name
    }));
    if (incoming.length) setPhotos(current => [...current, ...incoming]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const removePhoto = (id: string) => {
    setPhotos(current => {
      const target = current.find(photo => photo.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return current.filter(photo => photo.id !== id);
    });
  };
  const size = useMemo(() => loadSizes.find(option => option.id === sizeId) ?? loadSizes[1], [sizeId]);
  const toggleJob = (job: string) => {
    setSelectedJobs(current => current.includes(job) ? current.filter(item => item !== job) : [...current, job]);
  };
  const canSubmit = name.trim().length > 1 && phone.trim().length >= 10;
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitted(true);
  };
  if (submitted) {
    return <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.22,
      ease: [0.23, 1, 0.32, 1]
    }} role="status" className="px-6 py-10 text-center sm:px-8" data-id="element-215" data-mp="3fxtja6g581kd">
        <CheckCircle2Icon className="mx-auto h-12 w-12 text-ember-600" aria-hidden="true" data-id="element-216" data-mp="1glqqu6pjjwuk" />
        <p className="mt-4 font-display text-2xl uppercase leading-tight text-forest-800" data-id="element-217" data-mp="3s940n9esb4tn">
          Request received{name.trim() ? `, ${name.trim().split(' ')[0]}` : ''}
        </p>
        <p className="mx-auto mt-2 max-w-md text-base text-ink-700" data-id="element-218" data-mp="14dn4ixo8mqo">
          Javier will call {phone} shortly about your {size.label.toLowerCase()} in {city}
          {photos.length > 0 ? `, and he'll review the ${photos.length === 1 ? 'photo' : `${photos.length} photos`} you sent` : ''}
          . Need it gone sooner? Call us right now and we&apos;ll sort it on the spot.
        </p>
        <a href={PHONE_HREF} className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-ember-500 px-6 py-3.5 font-display text-lg uppercase tracking-wide text-ink-900 transition-colors duration-150 ease-out hover:bg-ember-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-800" data-id="element-219" data-mp="30hxvufo4ciy1">
          <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-220" data-mp="12qlkyjlqguu5" />
          {PHONE_DISPLAY}
        </a>
      </motion.div>;
  }
  return <form onSubmit={handleSubmit} className="px-6 py-6 sm:px-8" aria-label="Request a free junk removal quote" data-id="element-221" data-mp="dywbmlohkzhn">
      <fieldset data-id="element-222" data-mp="429tw5rgioju">
        <legend className="font-display text-base uppercase text-forest-800" data-id="element-223" data-mp="41roaiiwmxz9">
          1. What needs to go?
        </legend>
        <div className="mt-3 flex flex-wrap gap-2" data-id="element-224" data-mp="3v943is0891cj">
          {jobTypes.map(job => {
          const active = selectedJobs.includes(job);
          return <button key={job} type="button" aria-pressed={active} onClick={() => toggleJob(job)} className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-800 ${active ? 'border-forest-800 bg-forest-800 text-white' : 'border-forest-100 bg-white text-forest-800 hover:bg-forest-50'}`} data-id="element-225" data-mp="3mg0uqx0j1co8">
                {active && <CheckIcon className="h-4 w-4 text-ember-500" aria-hidden="true" data-id="element-226" data-mp="1n0ft3b3z1ghb" />}
                {job}
              </button>;
        })}
        </div>
      </fieldset>

      <fieldset className="mt-6" data-id="element-227" data-mp="1x06pxjg087kk">
        <legend className="font-display text-base uppercase text-forest-800" data-id="element-228" data-mp="1wz5cj8bin8zo">
          2. Roughly how much is it?
        </legend>
        <div className="mt-3 grid gap-2 sm:grid-cols-2" data-id="element-229" data-mp="14b981hfpgcsc">
          {loadSizes.map(option => {
          const active = option.id === size.id;
          return <button key={option.id} type="button" aria-pressed={active} onClick={() => setSizeId(option.id)} className={`rounded-md border p-3.5 text-left transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-800 ${active ? 'border-forest-800 bg-forest-50' : 'border-forest-100 hover:bg-forest-50'}`} data-id="element-230" data-mp="1991ah5dz7has">
                <span className="flex items-center justify-between gap-3" data-id="element-231" data-mp="9hyuzatc5754">
                  <span className="font-display text-base uppercase text-forest-800" data-id="element-232" data-mp="n8twg21i8lyo">
                    {option.label}
                  </span>
                  {active && <CheckIcon className="h-4 w-4 shrink-0 text-ember-600" aria-hidden="true" data-id="element-233" data-mp="3qq2byf8rf9q8" />}
                </span>
                <span className="mt-1 block text-sm text-ink-700" data-id="element-234" data-mp="3mql6vmx8well">{option.description}</span>
                <span className="mt-2.5 block h-1.5 w-full overflow-hidden rounded-full bg-forest-100" aria-hidden="true" data-id="element-235" data-mp="4k6sxnyz84n7">
                  <span className="block h-full rounded-full bg-ember-500" style={{
                width: `${option.fill}%`
              }} data-id="element-236" data-mp="1fxrh2bt6uuul" />
                </span>
              </button>;
        })}
        </div>
      </fieldset>

      <fieldset className="mt-6" data-id="element-237" data-mp="2xw5yb6azce9p">
        <legend className="font-display text-base uppercase text-forest-800" data-id="element-238" data-mp="2xx8mh6gx42lx">
          3. Add photos <span className="text-ink-700" data-id="element-239" data-mp="2jx69nyngvzdj">(optional — gets you a faster price)</span>
        </legend>
        <p className="mt-1 text-sm text-ink-700" data-id="element-240" data-mp="2oupn4wccc84f">
          A quick snapshot of the pile tells Javier more than any description. Up to {MAX_PHOTOS}{' '}
          photos.
        </p>

        <input ref={fileInputRef} id="quote-photos" type="file" accept="image/*" multiple capture="environment" onChange={event => addPhotos(event.target.files)} className="sr-only" data-id="element-241" data-mp="3gn6tdj8zbpnz" />

        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4" data-id="element-242" data-mp="37s0kgbeewrh8">
          {photos.map(photo => <div key={photo.id} className="relative aspect-square overflow-hidden rounded-md border border-forest-100" data-id="element-243" data-mp="45a0mdk5n5kl">
              <img src={photo.url} alt={photo.name} className="h-full w-full object-cover" data-id="element-244" data-mp="209zj38udemnq" />
              <button type="button" onClick={() => removePhoto(photo.id)} aria-label={`Remove ${photo.name}`} className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-ink-900/80 text-white transition-colors duration-150 ease-out hover:bg-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ember-500" data-id="element-245" data-mp="1vg3q6ksyrpzf">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-246" data-mp="2e6d42ry4566y" />
              </button>
            </div>)}

          {photos.length < MAX_PHOTOS && <label htmlFor="quote-photos" className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed border-forest-100 bg-forest-50 text-center text-xs font-semibold text-forest-800 transition-colors duration-150 ease-out hover:border-forest-700 hover:bg-forest-100 focus-within:ring-2 focus-within:ring-forest-800" data-id="element-247" data-mp="256q1w3xk7wcu">
              {photos.length === 0 ? <CameraIcon className="h-6 w-6 text-ember-600" aria-hidden="true" data-id="element-248" data-mp="3efujxfrsm0ur" /> : <ImagePlusIcon className="h-6 w-6 text-ember-600" aria-hidden="true" data-id="element-249" data-mp="3j7cmg89gs9hu" />}
              {photos.length === 0 ? 'Add photo' : 'Add more'}
            </label>}
        </div>
      </fieldset>

      <fieldset className="mt-6" data-id="element-250" data-mp="3t3xpwp5u5cjk">
        <legend className="font-display text-base uppercase text-forest-800" data-id="element-251" data-mp="3t73hcb1xsqig">
          4. Where and when?
        </legend>
        <div className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-252" data-mp="60avcu22h6m1">
          <label className="block" data-id="element-253" data-mp="5zrms3yb3uko">
            <span className="mb-1.5 block text-sm font-semibold text-ink-700" data-id="element-254" data-mp="22t2z9hgykqsl">City</span>
            <select value={city} onChange={event => setCity(event.target.value)} className={fieldClass} data-id="element-255" data-mp="27rxs7c8n3vqf">
              {serviceAreas.map(area => <option key={area} value={area} data-id="element-256" data-mp="3tsmrkm73hq12">
                  {area}
                </option>)}
              <option value="Other North County" data-id="element-257" data-mp="2mbypdc8nvkt">Other North County</option>
            </select>
          </label>
          <label className="block" data-id="element-258" data-mp="7mpunx0kff2h">
            <span className="mb-1.5 block text-sm font-semibold text-ink-700" data-id="element-259" data-mp="7jljgfoonj2b">
              How soon do you need it gone?
            </span>
            <select value={timing} onChange={event => setTiming(event.target.value)} className={fieldClass} data-id="element-260" data-mp="glus100e52d2">
              {timings.map(option => <option key={option} value={option} data-id="element-261" data-mp="qcfq8mc5k4vg">
                  {option}
                </option>)}
            </select>
          </label>
        </div>
      </fieldset>

      <fieldset className="mt-6" data-id="element-262" data-mp="zccl55p4xzyy">
        <legend className="font-display text-base uppercase text-forest-800" data-id="element-263" data-mp="zddvf7ybuifm">
          5. Where do we call you?
        </legend>
        <div className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-264" data-mp="2jsk2zi412agm">
          <label className="block" data-id="element-265" data-mp="2fsianzk6mte0">
            <span className="mb-1.5 block text-sm font-semibold text-ink-700" data-id="element-266" data-mp="2fve6nicgz7zt">Your name</span>
            <input type="text" value={name} onChange={event => setName(event.target.value)} placeholder="Maria G." required className={fieldClass} data-id="element-267" data-mp="2awb4c5nvjfvj" />
          </label>
          <label className="block" data-id="element-268" data-mp="2ykuekqqhi2q9">
            <span className="mb-1.5 block text-sm font-semibold text-ink-700" data-id="element-269" data-mp="2ylvxfhdqrtij">Phone number</span>
            <input type="tel" value={phone} onChange={event => setPhone(event.target.value)} placeholder="(760) 000-0000" required className={fieldClass} data-id="element-270" data-mp="2tqzsjgnepbo0" />
          </label>
        </div>
        <label className="mt-3 block" data-id="element-271" data-mp="3hhcbdmk6wstd">
          <span className="mb-1.5 block text-sm font-semibold text-ink-700" data-id="element-272" data-mp="1fld6x89kxevc">
            Anything else? (optional)
          </span>
          <textarea value={details} onChange={event => setDetails(event.target.value)} rows={2} placeholder="Stairs, gate code, heavy items, best time to call…" className={`${fieldClass} resize-none`} data-id="element-273" data-mp="1jlndon5749b9" />
        </label>
      </fieldset>

      <button type="submit" disabled={!canSubmit} className="mt-6 flex w-full items-center justify-center gap-3 rounded-md bg-ember-500 px-6 py-4 font-display text-xl uppercase tracking-wide text-ink-900 transition-colors duration-150 ease-out hover:bg-ember-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-800 disabled:cursor-not-allowed disabled:bg-forest-100 disabled:text-ink-700" data-id="element-274" data-mp="vwckbqq6dh75">
        <SendIcon className="h-5 w-5" aria-hidden="true" data-id="element-275" data-mp="2h8md35n9vvcu" />
        Send my free quote request
      </button>
      <p className="mt-3 text-center text-sm text-ink-700" data-id="element-276" data-mp="2wdi3kitng5ux">
        No spam, no obligation. Prefer to talk?{' '}
        <a href={PHONE_HREF} className="font-semibold text-forest-800 underline decoration-ember-500 decoration-2 underline-offset-4" data-id="element-277" data-mp="2renmpmf8srdg">
          {PHONE_DISPLAY}
        </a>
      </p>
    </form>;
}